import { forwardRef, useState, useRef, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'
import { Formik } from 'formik'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Spinner from 'react-bootstrap/Spinner'
import Image from 'react-bootstrap/Image'
import { PlusCircleFill } from 'react-bootstrap-icons'
import format from 'date-fns/format'
import _ from 'lodash'
import qs from 'query-string'

import { checkExistingTestConfig, GenderChoices, Genders, HowDidYouKnowUs, MOBILE_NUMBER_REGEX, ServiceProviderStatus } from '../../components/common'

import DynamicTable from '../../components/DynamicTable'
import { DashboardActions, DashboardResources, selectDashboard } from '../../redux/reducers/dashboardSlice'
import Tabs from '../components/Tabs'
import DetailedView from '../layout/DetailedView'
import IdentityInput from '../components/IdentityInput'
import Choice from '../../components/basic/Choice'
import Attachments from '../../components/basic/Attachments'
import { CityAreaSubform } from '../../components/LocationInfoForm'
import Select from '../../components/basic/Select'
import CertificatesFormGroup from '../components/CertificatesFormGroup'

const Actions = Object.freeze({
  CREATE: 'Create',
  UPDATE: 'Update'
})

const userType = 'ServiceProvider'

const schema = yup.object().shape({
  id: IdentityInput.schema,
  firstName: yup.string().required(),
  middleName: yup.string().nullable(),
  lastName: yup.string().required(),
  mobileNumber: yup.string().required()
    .matches(MOBILE_NUMBER_REGEX, 'invalid mobile number')
    .test(checkExistingTestConfig('mobileNumber', userType)),
  status: yup.string().required().oneOf(ServiceProviderStatus.getValues()).default(ServiceProviderStatus.ACTIVE.value),
  degree: yup.string().required(),
  jobTitle: yup.string().nullable(),
  gender: yup.string().oneOf(GenderChoices.getValues()),
  birthDate: yup.date().nullable(),
  nationalIds: yup.lazy(val => {
    if(val && val.length > 0) return yup.array(yup.number())
    .min(2, 'You have to upload 2 national id photos')
    .max(2, 'You have to upload 2 national id photos');
    else return yup.array(yup.number()).nullable();
  }),
  yearsOfExperience: yup.number().min(0).nullable(),
  homeVisitFees: yup.number().min(0).nullable(),
  cities: yup.array(yup.number()).default([]),
  areas: yup.array(yup.number()).default([]),
  howDidYouKnowAboutUs: yup.string().nullable(),
  otherComments: yup.string().nullable(),
  whatsAppNumber: yup.string().nullable(),
  certificates: yup.array(yup.string()).nullable(),
  license: yup.string().nullable(),
  expiryDate: yup.date().nullable(),
  speciality: yup.string().nullable(),
  timing: yup.string().nullable()
})

const Create = forwardRef(({ action }, ref) => {
  const dispatch = useDispatch()
  const [certificatesInputs, setCertificatesInputs] = useState(0)

  const submit = (body) => {
    const dispatchAction = action === Actions.CREATE ? DashboardActions.createRequest : DashboardActions.updateRequest

    dispatch(dispatchAction({ resource: DashboardResources.PHYSICAL_THERAPISTS, body }))
  }

  const incrementCertificatesInputs = () => setCertificatesInputs(x => x + 1)

  return (
    <Formik
      validationSchema={schema}
      initialValues={schema.default()}
      validateOnChange={false}
      innerRef={ref}
      onSubmit={submit}
    >
      {({ handleSubmit,handleChange, values, errors, setFieldValue }) => (
        <Form noValidate onSubmit={handleSubmit}>
          <Form.Group>
            <Form.Label>ID</Form.Label>
            <IdentityInput
              prefix='PH'
              onChange={v => setFieldValue('id', v)}
              isInvalid={errors.id}
              errorMessage={errors.id}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>First Name</Form.Label>
            <Form.Control
              name='firstName'
              value={values.firstName}
              isInvalid={errors.firstName}
              onChange={handleChange}
            />
            <Form.Control.Feedback type='invalid'>
              {errors.firstName}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label>Middle Name</Form.Label>
            <Form.Control
              name='middleName'
              value={values.middleName}
              isInvalid={errors.middleName}
              onChange={handleChange}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Last Name</Form.Label>
            <Form.Control
              name='lastName'
              value={values.lastName}
              isInvalid={errors.lastName}
              onChange={handleChange}
            />
            <Form.Control.Feedback type='invalid'>
              {errors.lastName}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label>Mobile Number</Form.Label>
            <Form.Control
              name='mobileNumber'
              value={values.mobileNumber}
              isInvalid={errors.mobileNumber}
              onChange={handleChange}
            />
            <Form.Control.Feedback type='invalid'>
              {errors.mobileNumber}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label>Status</Form.Label>
            <Form.Select
              name='status'
              value={values.status}
              onChange={handleChange}
            >
              {ServiceProviderStatus.getValues().map(value => (
                <option key={value} value={value}>{ServiceProviderStatus.getTextByValue(value)}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Degree</Form.Label>
            <Form.Control
              name='degree'
              value={values.degree}
              isInvalid={errors.degree}
              onChange={handleChange}
            />
            <Form.Control.Feedback type='invalid'>
              {errors.degree}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label>Job Title</Form.Label>
            <Form.Control
              name='jobTitle'
              value={values.jobTitle}
              isInvalid={errors.jobTitle}
              onChange={handleChange}
            />
            <Form.Control.Feedback type='invalid'>
              {errors.jobTitle}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label>Gender</Form.Label>
            <Choice
              value={values.gender}
              options={Genders.getValues()}
              onChange={value => setFieldValue('gender', value)}
            />
            <Form.Control.Feedback type='invalid'>
              {errors.gender}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label>Birthdate</Form.Label>
            <Form.Control
              name='birthDate'
              type='date'
              value={values.birthDate}
              onChange={handleChange}
              isInvalid={errors.birthDate}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>National ID</Form.Label>
            <Attachments
              single={false}
              initialValue={values.nationalIds}
              onChange={value => setFieldValue('nationalIds', value?.map(nid => nid.id))}
              isInvalid={errors.nationalIds}
              errorMessage={errors.nationalIds}
              target={`nationalIds`}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Years Of Experience</Form.Label>
            <Form.Control
              type='number'
              name='yearsOfExperience'
              value={values.yearsOfExperience}
              isInvalid={errors.yearsOfExperience}
              onChange={handleChange}
            />
            <Form.Control.Feedback type='invalid'>
              {errors.yearsOfExperience}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label>Fees</Form.Label>
            <Form.Control
              type='number'
              name='homeVisitFees'
              value={values.homeVisitFees}
              isInvalid={errors.homeVisitFees}
              onChange={handleChange}
            />
            <Form.Control.Feedback type='invalid'>
              {errors.homeVisitFees}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label>Timing</Form.Label>
            <Form.Control
              name='timing'
              value={values.timing}
              isInvalid={errors.timing}
              onChange={handleChange}
            />
          </Form.Group>
          <CityAreaSubform
            multiple
            labels={{
              city: 'Cities',
              area: 'Areas'
            }}
            values={values}
            errors={errors}
            onAreaChange={value => setFieldValue('areas', value)}
            onCityChange={value => setFieldValue('cities', value)}
          />
          <Form.Group>
            <Form.Label>How did you know about us?</Form.Label>
            <Select
              name='howDidYouKnowAboutUs'
              label='How did you know about us?'
              options={Object.values(HowDidYouKnowUs).map(value => ({ text: value, value }))}
              value={values.howDidYouKnowAboutUs}
              onSelect={value => setFieldValue('howDidYouKnowAboutUs', value, false)}
              isInvalid={errors.howDidYouKnowAboutUs}
              other
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Other Comments</Form.Label>
            <Form.Control
              name='otherComments'
              value={values.otherComments}
              isInvalid={errors.otherComments}
              onChange={handleChange}
            />
          </Form.Group>
          <CertificatesFormGroup
            certificates={values.certificates}
            onChange={v => setFieldValue('certificates', v)}
          />
          <Form.Group>
            <Form.Label>License</Form.Label>
            <Form.Control
              name='license'
              value={values.license}
              isInvalid={errors.license}
              onChange={handleChange}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Expiry Date</Form.Label>
            <Form.Control
              type='date'
              name='expiryDate'
              value={values.expiryDate}
              isInvalid={errors.expiryDate}
              onChange={handleChange}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Speciality</Form.Label>
            <Form.Control
              name='speciality'
              value={values.speciality}
              isInvalid={errors.speciality}
              onChange={handleChange}
            />
          </Form.Group>
        </Form>
      )}
    </Formik>
  )
})

const PhysicalTherapists = () => {
  const { physicalTherapists, isLoading } = useSelector(selectDashboard)
  const [modalAction, setModalAction] = useState(null)
  const submitRef = useRef()
  const { id } = qs.parse(document.location.search)

  const closeModal = () => setModalAction(null)

  useEffect(() => {
    if (!isLoading) {
      setModalAction(null)
    }
  }, [isLoading])

  return (
    <div>
      <div className='sub-screen-title'>
        <h1>Physical Therapists</h1>
        <Button float='right' onClick={() => setModalAction(Actions.CREATE)}><PlusCircleFill /> Create</Button>
      </div>

      <Tabs>
        {/* <Tabs.Tab title='Pending'>
          <DynamicTable
            search
            resource={DashboardResources.PHYSICAL_THERAPISTS}
            data={physicalTherapists.items}
            mapper={item => {
              return [
                { key: 'id', name: 'ID', value: item.id },
                { key: 'name', sort: 'firstName,middleName,lastName', name: 'Name', value: [item.firstName, item.middleName, item.lastName].join(' ') },
                { key: 'mobileNumber', name: 'Number', value: item.mobileNumber },
                { key: 'degree', name: 'Degree', value: item.degree },
                { key: 'speciality', name: 'Speciality', value: item.speciality },
                { key: 'yearsOfExperience', name: 'Years of Exp.', value: item.yearsOfExperience },
                { key: 'cityName', sort: 'cities.englishName', name: 'Cities', value: item.cities.map(city => city.englishName).join(', ') },
                { key: 'areaName', sort: 'areas.englishName', name: 'Areas', value: item.areas.map(area => area.englishName).join(', ') },
                { key: 'homeVisitFees', name: 'Fees', value: item.homeVisitFees },
                { key: 'status', name: 'Status', value: item.status }
              ]
            }}
            pagesCount={physicalTherapists.pagesCount}
          />
        </Tabs.Tab> */}
        <Tabs.Tab title='Completed'>
          <DynamicTable
            search
            sortable
            resource={DashboardResources.PHYSICAL_THERAPISTS}
            data={physicalTherapists.items}
            headers={[
              { key: 'id', name: 'ID', sort: 'id.prefix,id.order' },
              { key: 'name', name: 'Name', sort: 'firstName,middleName,lastName' },
              { key: 'mobileNumber', name: 'Number' },
              { key: 'degree', name: 'Degree' },
              { key: 'speciality', name: 'Speciality' },
              { key: 'yearsOfExperience', name: 'Years of Exp.' },
              { key: 'cityName', name: 'Cities', sort: '_citiesCount' },
              { key: 'areaName', name: 'Areas', sort: '_areasCount' },
              { key: 'homeVisitFees', name: 'Fees' },
              { key: 'totalRequests', name: 'Total Requests' },
              { key: 'activeRequests', name: 'Active Requests' },
              { key: 'rating', name: 'Rating' },
              { key: 'userApproved', name: 'User Approved' },
              { key: 'dateOfApproval', name: 'Date of Approval' },
            ]}
            mapper={item => {
              return [
                { key: 'id', value: item.id },
                { key: 'name', value: [item.firstName, item.middleName, item.lastName].join(' ') },
                { key: 'mobileNumber', value: item.mobileNumber },
                { key: 'degree', value: item.degree },
                { key: 'speciality', value: item.speciality },
                { key: 'yearsOfExperience', value: item.yearsOfExperience },
                { key: 'cityName', value: item.cities?.map(city => city.arabicName).join(', ') },
                { key: 'areaName', value: item.areas?.map(area => area.arabicName).join(', ') },
                { key: 'homeVisitFees', value: item.homeVisitFees },
                { key: 'totalRequests', value: item.totalRequests },
                { key: 'activeRequests', value: item.activeRequests },
                { key: 'rating', value: item.rating },
                { key: 'userApproved', value: item.userApproved?.firstName },
                { key: 'dateOfApproval', value: item.userApproved?.firstName },
              ]
            }}
            pagesCount={physicalTherapists.pagesCount}
            detail
            initialFilters={[_.omitBy({ id }, _.isNil)]}
          />
        </Tabs.Tab>
      </Tabs>
      <Modal show={!!modalAction} dialogClassName='ltr dashboard-layout' onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title>{modalAction} Physical Therapist</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Create ref={submitRef} action={modalAction} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => submitRef.current?.submitForm()}>
            {isLoading ? <Spinner animation="border" size='sm' /> : modalAction}
          </Button>
          <Button variant="outline-primary" onClick={closeModal}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

PhysicalTherapists.Detail = () => {
  const { id } = useParams()
  const dispatch = useDispatch()

  const handleUpdate = initialValue => body => {
    body.id = id
    body.deletedFields = _.keys(initialValue).filter(key => _.isNil(body[key]) && !_.isNil(initialValue[key]))

    dispatch(DashboardActions.updateRequest({ resource: DashboardResources.PHYSICAL_THERAPISTS, body }))
  }

  return (
    <DetailedView
      id={id}
      serviceProvider
      resource={DashboardResources.PHYSICAL_THERAPISTS}
      sectionMappers={[
        physicalTherapist => ({
          title: 'Profile Information',
          body: {
            "Name": [physicalTherapist.firstName, physicalTherapist.middleName, physicalTherapist.lastName].filter(n => n).join(' '),
            "Mobile Number": physicalTherapist.mobileNumber,
            "Gender": physicalTherapist.gender,
            "Email": physicalTherapist.email,
            "Birthdate": physicalTherapist.birthDate && format(Date.parse(physicalTherapist.birthDate), 'dd/MM/yyyy')
          },
          edit: () => ref => {
            const fields = ['firstName', 'middleName', 'lastName', 'mobileNumber', 'gender', 'email', 'birthDate']
            const initialValues = _.pick(physicalTherapist, fields, 'id')
            const _schema = yup.object().shape({
              ...schema.pick(fields).fields,
              gender: yup.string().oneOf(Genders.getValues()).nullable(),
              email: yup.string().email().nullable().test(checkExistingTestConfig('email', userType)),
              birthDate: yup.date().nullable()
            })

            return (
              <Formik
                innerRef={ref}
                initialValues={initialValues}
                validationSchema={_schema}
                onSubmit={handleUpdate(initialValues)}
              >
                {({ handleSubmit, handleChange, setFieldValue, values, errors }) =>(
                  <Form noValidate onSubmit={handleSubmit}>
                    <Form.Group>
                      <Form.Label>First Name</Form.Label>
                      <Form.Control
                        name='firstName'
                        value={values.firstName}
                        isInvalid={errors.firstName}
                        onChange={handleChange}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.firstName}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Middle Name</Form.Label>
                      <Form.Control
                        name='middleName'
                        value={values.middleName}
                        isInvalid={errors.middleName}
                        onChange={handleChange}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.middleName}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Last Name</Form.Label>
                      <Form.Control
                        name='lastName'
                        value={values.lastName}
                        isInvalid={errors.lastName}
                        onChange={handleChange}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.lastName}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Mobile Number</Form.Label>
                      <Form.Control
                        name='mobileNumber'
                        value={values.mobileNumber}
                        isInvalid={errors.mobileNumber}
                        onChange={handleChange}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.mobileNumber}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Gender</Form.Label>
                      <Choice
                        value={values.gender}
                        options={Genders.getValues()}
                        onChange={value => setFieldValue('gender', value)}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        name='email'
                        value={values.email}
                        isInvalid={errors.email}
                        onChange={handleChange}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.email}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Birthdate</Form.Label>
                      <Form.Control
                        type='date'
                        name='birthDate'
                        value={values.birthDate}
                        isInvalid={errors.birthDate}
                        onChange={handleChange}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.birthDate}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Form>
                )}
              </Formik>
            )
          }
        }),
        physicalTherapist => ({
          title: 'Occupation Information',
          body: {
            "Degree": physicalTherapist.degree,
            "Speciality": physicalTherapist.speciality,
            "Job Title": physicalTherapist.jobTitle,
            "Years of experience": physicalTherapist.yearsOfExperience,
            "License": physicalTherapist.license,
            "Certificates": physicalTherapist.certificates.join(' - '),
            "Expiry Date": physicalTherapist.expiryDate && format(Date.parse(physicalTherapist.expiryDate), 'dd/MM/yyyy')
          },
          edit: () => ref => {
            const fields = [
              'degree',
              'speciality',
              'jobTitle',
              'yearsOfExperience',
              'certificates',
              'license',
              'expiryDate'
            ]
            const initialValues = _.pick(physicalTherapist, fields)
            const _schema = schema.pick(fields)

            return (
              <Formik
                innerRef={ref}
                initialValues={initialValues}
                validationSchema={_schema}
                onSubmit={handleUpdate(initialValues)}
              >
                {({ handleSubmit, handleChange, setFieldValue, values, errors }) =>(
                  <Form noValidate onSubmit={handleSubmit}>
                    <Form.Group>
                      <Form.Label>Job Title</Form.Label>
                      <Form.Control
                        name='jobTitle'
                        value={values.jobTitle}
                        isInvalid={errors.jobTitle}
                        onChange={handleChange}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.jobTitle}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Speciality</Form.Label>
                      <Form.Control
                        name='speciality'
                        value={values.speciality}
                        isInvalid={errors.speciality}
                        onChange={handleChange}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Degree</Form.Label>
                      <Form.Control
                        name='degree'
                        value={values.degree}
                        isInvalid={errors.degree}
                        onChange={handleChange}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Years Of Experience</Form.Label>
                      <Form.Control
                        type='number'
                        name='yearsOfExperience'
                        value={values.yearsOfExperience}
                        isInvalid={errors.yearsOfExperience}
                        onChange={handleChange}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.yearsOfExperience}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <CertificatesFormGroup
                      certificates={values.certificates}
                      onChange={v => setFieldValue('certificates', v)}
                    />
                    <Form.Group>
                      <Form.Label>License</Form.Label>
                      <Form.Control
                        name='license'
                        value={values.license}
                        isInvalid={errors.license}
                        onChange={handleChange}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Expiry Date</Form.Label>
                      <Form.Control
                        type='date'
                        name='expiryDate'
                        value={values.expiryDate}
                        isInvalid={errors.expiryDate}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Form>
                )}
              </Formik>
            )
          }
        }),
        physicalTherapist => ({
          title: 'Work Shifts',
          body: {
            "Timing": physicalTherapist.timing
          },
          edit: () => ref => {
            const fields = [
              'timing',
            ]
            const initialValues = _.pick(physicalTherapist, fields)
            const _schema = schema.pick(fields)

            return (
              <Formik
                innerRef={ref}
                initialValues={initialValues}
                validationSchema={_schema}
                onSubmit={handleUpdate(initialValues)}
              >
                {({ handleSubmit, handleChange, values, errors }) =>(
                  <Form noValidate onSubmit={handleSubmit}>
                    <Form.Group>
                      <Form.Label>Timing</Form.Label>
                      <Form.Control
                        name='timing'
                        value={values.timing}
                        isInvalid={errors.timing}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Form>
                )}
              </Formik>
            )
          }
        }),
        physicalTherapist => ({
          title: 'Covered Areas & Timing',
          body: {
            "Cities": physicalTherapist.cities.map(city => city.arabicName).join(' - '),
            "Areas": physicalTherapist.areas.map(area => area.arabicName).join(' - '),
            "Visit Fees": physicalTherapist.homeVisitFees
          },
          edit: () => ref => {
            const fields = [
              'homeVisitFees',
              'cities',
              'areas'
            ]
            const initialValues = _.mapValues(_.pick(physicalTherapist, fields), (v, k) => ['cities', 'areas'].includes(k) ? v.map(vv => vv.id) : v)
            const _schema = schema.pick(fields)

            return (
              <Formik
                innerRef={ref}
                initialValues={initialValues}
                validationSchema={_schema}
                onSubmit={handleUpdate(initialValues)}
              >
                {({ handleSubmit, handleChange, setFieldValue, values, errors }) =>(
                  <Form noValidate onSubmit={handleSubmit}>
                    <Form.Group>
                      <Form.Label>Fees</Form.Label>
                      <Form.Control
                        type='number'
                        name='homeVisitFees'
                        value={values.homeVisitFees}
                        isInvalid={errors.homeVisitFees}
                        onChange={handleChange}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.homeVisitFees}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <CityAreaSubform
                      multiple
                      labels={{
                        city: 'Cities',
                        area: 'Areas'
                      }}
                      values={values}
                      errors={errors}
                      onAreaChange={value => setFieldValue('areas', value)}
                      onCityChange={value => setFieldValue('cities', value)}
                    />
                  </Form>
                )}
              </Formik>
            )
          }
        }),
        physicalTherapist => ({
          title: 'Attachments',
          body: {
            "National ID": (
              <>
                <Image src={physicalTherapist.nationalIds?.[0]?.url} />,
                <Image src={physicalTherapist.nationalIds?.[1]?.url} />,
              </>
            )
          },
          edit: () => ref => {
            const fields = [
              'nationalIds',
            ]
            const initialValues = _.pick(physicalTherapist, fields)
            const _schema = schema.pick(fields)

            return (
              <Formik
                innerRef={ref}
                initialValues={initialValues}
                validationSchema={_schema}
                onSubmit={handleUpdate(initialValues)}
              >
                {({ handleSubmit, setFieldValue, values, errors }) =>(
                  <Form noValidate onSubmit={handleSubmit}>
                    <Form.Group>
                      <Form.Label>National ID</Form.Label>
                      <Attachments
                        single={false}
                        initialValue={values.nationalIds}
                        onChange={value => setFieldValue('nationalIds', value?.map(nid => nid.id))}
                        isInvalid={errors.nationalIds}
                        errorMessage={errors.nationalIds}
                        target={`nationalIds`}
                      />
                    </Form.Group>
                  </Form>
                )}
              </Formik>
            )
          }
        })
      ]}
    />
  )
}

export default PhysicalTherapists
